import React from 'react';

export interface TrackablePlaceholderProps {
  /**
   * Opaque `_meta.*` payload collected by the binding resolver. Held
   * here as a data reference so DB-2.4 integration tests can assert
   * component-level metadata without reaching into the HOC internals.
   * The placeholder itself never inspects the value.
   */
  readonly meta?: Readonly<Record<string, unknown>>;
  readonly children: React.ReactNode;
}

/**
 * Default pass-through wrapper emitted by {@link createBoundRender} so
 * the binding enhancer satisfies child SAD §9.6 without depending on
 * the real `<Trackable>` analytics wrapper.
 *
 * DB-2.4 (EDUN-10692) replaces this implementation with a wrapper that
 * bridges into `EventingProvider` / `TrackingProvider`. At that point
 * the HOC does not change — only the resolved import does. The
 * placeholder intentionally carries `data-edu-trackable="placeholder"`
 * so downstream tests can assert the real Trackable ships by searching
 * for its absence.
 */
export const TrackablePlaceholder: React.FC<TrackablePlaceholderProps> = ({
  meta,
  children,
}) => {
  const hasMeta = meta !== undefined && Object.keys(meta).length > 0;
  return (
    <React.Fragment>
      {hasMeta ? (
        // `display: contents` keeps the placeholder layout-neutral so
        // bound components render identically to their unbound form.
        // **Accessibility note for DB-2.4 (EDUN-10692).** `display:
        // contents` is known to remove the element from the
        // accessibility tree in some browser/AT combinations (older
        // Safari + VoiceOver in particular). The placeholder is
        // metadata-only and the `<span>` carries no semantics today,
        // so the practical impact is nil — but the real
        // `<Trackable>` analytics wrapper that replaces this in
        // DB-2.4 **must not** carry the `display: contents` pattern
        // forward and should instead participate in the live region
        // / role tree it actually represents.
        <span
          data-edu-trackable='placeholder'
          data-edu-meta-keys={Object.keys(meta ?? {}).join(',')}
          style={{ display: 'contents' }}
        >
          {children}
        </span>
      ) : (
        children
      )}
    </React.Fragment>
  );
};
